/**
 * @fileoverview Funções para comparação de versões (diff)
 * @module lib/dre/versionDiff
 * 
 * Implementa lógica de comparação entre versões de DRE
 * para gerar diffs e permitir restauração de versões.
 */

import type { DREData, DREYear, DREEditableField } from '../types/dre';
import type { VersionDiff } from '../types/version';
import { DRE_LINES } from '../types/dre';

// ============================================================================
// FUNÇÕES DE DIFF
// ============================================================================

/**
 * Calcula diferenças entre duas versões de DRE
 * 
 * @param oldData - DRE anterior
 * @param newData - DRE nova
 * @returns Array de diferenças encontradas
 * 
 * @example
 * ```ts
 * const diff = calculateDiff(oldVersion.dreData, newVersion.dreData);
 * // [
 * //   { company: 'setima', year: 2024, field: 'receitaBruta', oldValue: 5000000, newValue: 5500000, ... },
 * //   ...
 * // ]
 * ```
 */
export function calculateDiff(oldData: DREData, newData: DREData): VersionDiff[] {
  const diffs: VersionDiff[] = [];

  // Comparar Sétima
  const setimaDiffs = compareDRECompany(
    oldData.setima.years,
    newData.setima.years,
    'setima',
    oldData.setima.companyName
  );
  diffs.push(...setimaDiffs);

  // Comparar 555 Studios
  const studio555Diffs = compareDRECompany(
    oldData.studio555.years,
    newData.studio555.years,
    '555',
    oldData.studio555.companyName
  );
  diffs.push(...studio555Diffs);

  return diffs;
}

/**
 * Compara anos de uma empresa específica
 */
function compareDRECompany(
  oldYears: DREYear[],
  newYears: DREYear[],
  company: 'setima' | '555',
  companyName: string
): VersionDiff[] {
  const diffs: VersionDiff[] = [];

  // Criar mapa de anos antigos
  const oldYearsMap = new Map(oldYears.map(y => [y.year, y]));
  const newYearsMap = new Map(newYears.map(y => [y.year, y]));

  // Comparar anos existentes em ambos
  newYears.forEach(newYear => {
    const oldYear = oldYearsMap.get(newYear.year);
    
    if (oldYear) {
      // Comparar campos editáveis apenas
      const yearDiffs = compareYears(oldYear, newYear, company, companyName);
      diffs.push(...yearDiffs);
    } else {
      // Ano novo adicionado - adicionar diff para todos os campos
      const yearDiffs = getNewYearDiffs(newYear, company, companyName);
      diffs.push(...yearDiffs);
    }
  });

  // Anos removidos
  oldYears.forEach(oldYear => {
    if (!newYearsMap.has(oldYear.year)) {
      const yearDiffs = getRemovedYearDiffs(oldYear, company, companyName);
      diffs.push(...yearDiffs);
    }
  });

  return diffs;
}

/**
 * Compara campos editáveis entre dois anos
 */
function compareYears(
  oldYear: DREYear,
  newYear: DREYear,
  company: 'setima' | '555',
  companyName: string
): VersionDiff[] {
  const diffs: VersionDiff[] = [];

  // Campos editáveis para comparar
  const editableFields: DREEditableField[] = [
    'receitaBruta',
    'impostosVendas',
    'custosDirectos',
    'despesasOperacionais',
    'depreciacao',
    'impostosLucro',
  ];

  editableFields.forEach((field: DREEditableField) => {
    const oldValue = oldYear[field];
    const newValue = newYear[field];

    // Só adicionar diff se houve mudança
    if (oldValue !== newValue) {
      const change = newValue - oldValue;
      const changePercent = oldValue !== 0 ? (change / oldValue) * 100 : 100;

      diffs.push({
        company,
        companyName,
        year: newYear.year,
        field,
        fieldLabel: getFieldLabel(field),
        oldValue,
        newValue,
        change: roundToTwo(change),
        changePercent: roundToTwo(changePercent),
        changeType: 'modified',
      });
    }
  });

  return diffs;
}

/**
 * Cria diffs para um ano novo adicionado
 */
function getNewYearDiffs(year: DREYear, company: 'setima' | '555', companyName: string): VersionDiff[] {
  const diffs: VersionDiff[] = [];

  const editableFields: DREEditableField[] = [
    'receitaBruta',
    'impostosVendas',
    'custosDirectos',
    'despesasOperacionais',
    'depreciacao',
    'impostosLucro',
  ];

  editableFields.forEach(field => {
    const value = year[field];
    
    if (value !== 0) {
      diffs.push({
        company,
        companyName,
        year: year.year,
        field,
        fieldLabel: getFieldLabel(field),
        oldValue: 0,
        newValue: value,
        change: value,
        changePercent: 100,
        changeType: 'added',
      });
    }
  });

  return diffs;
}

/**
 * Cria diffs para um ano removido
 */
function getRemovedYearDiffs(year: DREYear, company: 'setima' | '555', companyName: string): VersionDiff[] {
  const diffs: VersionDiff[] = [];

  const editableFields: DREEditableField[] = [
    'receitaBruta',
    'impostosVendas',
    'custosDirectos',
    'despesasOperacionais',
    'depreciacao',
    'impostosLucro',
  ];

  editableFields.forEach((field: DREEditableField) => {
    const value = year[field];
    
    if (value !== 0) {
      diffs.push({
        company,
        companyName,
        year: year.year,
        field,
        fieldLabel: getFieldLabel(field),
        oldValue: value,
        newValue: 0,
        change: -value,
        changePercent: -100,
        changeType: 'removed',
      });
    }
  });

  return diffs;
}

/**
 * Obtém label amigável de um campo
 */

/**
 * Obtém label amigável de um campo
 */
function getFieldLabel(field: keyof DREYear): string {
  const config = DRE_LINES.find((c) => c.field === field);
  return config?.label || field;
}

/**
 * Arredonda para 2 casas decimais
 */
function roundToTwo(num: number): number {
  return Math.round((num + Number.EPSILON) * 100) / 100;
}

// ============================================================================
// FUNÇÕES DE FORMATAÇÃO DE DIFF
// ============================================================================

/**
 * Formata diff para exibição
 * 
 * @example
 * ```ts
 * formatDiff(diff)
 * // "Sétima 2024 - Receita Bruta: R$ 5.000.000 → R$ 5.500.000 (+10.0%)"
 * ```
 */
export function formatDiff(diff: VersionDiff): string {
  const companyName = diff.company === 'setima' ? 'Sétima' : '555 Studios';
  const oldValueFormatted = formatCurrency(diff.oldValue);
  const newValueFormatted = formatCurrency(diff.newValue);
  const changeSign = diff.change >= 0 ? '+' : '';
  const changeFormatted = `${changeSign}${diff.changePercent.toFixed(1)}%`;

  return `${companyName} ${diff.year} - ${diff.fieldLabel}: ${oldValueFormatted} → ${newValueFormatted} (${changeFormatted})`;
}

/**
 * Agrupa diffs por empresa e ano
 */
export function groupDiffsByCompanyAndYear(
  diffs: VersionDiff[]
): Map<string, VersionDiff[]> {
  const grouped = new Map<string, VersionDiff[]>();

  diffs.forEach(diff => {
    const key = `${diff.company}-${diff.year}`;
    
    if (!grouped.has(key)) {
      grouped.set(key, []);
    }
    
    grouped.get(key)!.push(diff);
  });

  return grouped;
}

/**
 * Resume diffs (conta mudanças por tipo)
 */
export function summarizeDiffs(diffs: VersionDiff[]): {
  totalChanges: number;
  byCompany: { setima: number; studio555: number };
  byYear: Map<number, number>;
  significant: VersionDiff[]; // mudanças > 10%
} {
  const byCompany = { setima: 0, studio555: 0 };
  const byYear = new Map<number, number>();
  const significant: VersionDiff[] = [];

  diffs.forEach(diff => {
    // Contar por empresa
    if (diff.company === 'setima') {
      byCompany.setima++;
    } else {
      byCompany.studio555++;
    }

    // Contar por ano
    const yearCount = byYear.get(diff.year) || 0;
    byYear.set(diff.year, yearCount + 1);

    // Identificar mudanças significativas
    if (Math.abs(diff.changePercent) > 10) {
      significant.push(diff);
    }
  });

  return {
    totalChanges: diffs.length,
    byCompany,
    byYear,
    significant,
  };
}

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Formata número como moeda BRL
 */
function formatCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

/**
 * Verifica se duas DREs são idênticas
 */
export function areDREsEqual(dre1: DREData, dre2: DREData): boolean {
  const diff = calculateDiff(dre1, dre2);
  return diff.length === 0;
}

/**
 * Clona DREData profundamente
 */
export function cloneDREData(data: DREData): DREData {
  return {
    ...data,
    setima: {
      ...data.setima,
      years: data.setima.years.map(y => ({ ...y })),
    },
    studio555: {
      ...data.studio555,
      years: data.studio555.years.map(y => ({ ...y })),
    },
  };
}
